/**
 * ライセンス(契約している機能)の確認と貼り替え。suite:admin だけ。
 *
 * **JWT は表示しない。** 貼り付ける欄はあるが、いま入っているものは返ってこない
 * (顧客名・プラン・期限・状態だけ)。貼り替えはサーバーが署名を確かめてから
 * 保存するので、駄目なものは 400 で戻る。
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Descriptions, Flex, Input, Space, Spin, Table, Tag, Typography, message } from "antd";
import { useState } from "react";

import type { FeatureRow, LicenseState } from "../lib/api";
import { ApiError } from "../lib/api";
import { useApi } from "../lib/useApi";

const { Title, Paragraph, Text } = Typography;

const STATE_COLOR: Record<LicenseState["state"], string> = {
  ok: "green",
  grace: "orange",
  expired: "red",
  missing: "default",
  invalid: "red",
};

const APP_LABEL: Record<string, string> = {
  suite: "共通",
  hr: "人事",
  idp: "ID管理",
  mdm: "デバイス管理",
  dlp: "情報漏洩対策",
};

function date(value: string | null): string {
  return value ? value.slice(0, 10) : "—";
}

export function LicensePage() {
  const api = useApi();
  const qc = useQueryClient();
  const [jwt, setJwt] = useState("");
  const info = useQuery({ queryKey: ["license"], queryFn: api.license });

  const save = useMutation({
    mutationFn: (value: string) => api.saveLicense(value),
    onSuccess: async () => {
      setJwt("");
      void message.success("ライセンスを入れ替えた");
      // 機能が変わるとナビもタイルも変わる。`me` と `apps` も捨てる
      await qc.invalidateQueries({ queryKey: ["license"] });
      await qc.invalidateQueries({ queryKey: ["me"] });
      await qc.invalidateQueries({ queryKey: ["apps"] });
    },
    onError: (err) => {
      void message.error(err instanceof ApiError ? err.message : String(err));
    },
  });

  if (info.isLoading)
    return (
      <Flex align="center" justify="center" style={{ padding: 64 }}>
        <Spin />
      </Flex>
    );

  if (info.isError)
    return (
      <Alert
        type="error"
        showIcon
        style={{ margin: 24 }}
        message="ライセンスの状態を取得できなかった"
        description={info.error instanceof Error ? info.error.message : String(info.error)}
      />
    );

  const lic = info.data!.license;
  const rows = info.data!.features;
  const enabled = rows.filter((r) => r.enabled).length;

  return (
    <div style={{ padding: 24, maxWidth: 1080, margin: "0 auto" }}>
      <Title level={3}>ライセンス</Title>
      <Paragraph type="secondary">
        契約している機能。ここに無い機能は画面にも API にも出ない(403)。
      </Paragraph>

      {lic.state !== "ok" && (
        <Alert
          style={{ marginBottom: 16 }}
          type={lic.state === "grace" ? "warning" : "error"}
          showIcon
          data-testid="license-alert"
          message={lic.state_label}
          description={lic.problem || undefined}
        />
      )}

      <Card style={{ marginBottom: 24 }}>
        <Descriptions column={{ xs: 1, sm: 2, lg: 3 }}>
          <Descriptions.Item label="状態">
            <Tag color={STATE_COLOR[lic.state]} data-testid="license-state-tag">
              {lic.state_label}
            </Tag>
            {lic.read_only && <Tag color="red">参照のみ</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="契約者">{lic.customer || "—"}</Descriptions.Item>
          <Descriptions.Item label="プラン">{lic.plan || "—"}</Descriptions.Item>
          <Descriptions.Item label="期限">{date(lic.expires_at)}</Descriptions.Item>
          <Descriptions.Item label="猶予">{date(lic.grace_until)}</Descriptions.Item>
          <Descriptions.Item label="有効な機能">
            <span data-testid="license-enabled-count">
              {enabled} / {rows.length}
            </span>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Title level={4}>機能</Title>
      {/* antd の Table は data-* を DOM に流さないので、囲いの div に付ける */}
      <div data-testid="feature-table">
        <Table<FeatureRow>
          scroll={{ x: "max-content" }}
          rowKey="key"
          size="small"
          pagination={false}
          dataSource={rows}
          columns={[
            {
              title: "アプリ",
              dataIndex: "app",
              width: 160,
              render: (app: string) => APP_LABEL[app] ?? app,
            },
            { title: "機能", width: 240, dataIndex: "label" },
            {
              title: "キー",
              width: 280,
              dataIndex: "key",
              render: (key: string) => <Text code>{key}</Text>,
            },
            {
              title: "必要な機能",
              width: 360,
              dataIndex: "depends",
              render: (depends: string[]) =>
                depends.length > 0 ? <Text type="secondary">{depends.join(", ")}</Text> : "—",
            },
            {
              title: "状態",
              dataIndex: "enabled",
              width: 110,
              render: (on: boolean, row: FeatureRow) => (
                <Tag color={on ? "green" : "default"} data-testid={`feature-${row.key}`}>
                  {on ? "有効" : "無効"}
                </Tag>
              ),
            },
          ]}
        />
      </div>

      <Title level={4} style={{ marginTop: 32 }}>
        貼り替え
      </Title>
      <Paragraph type="secondary">
        発行されたライセンス(1 行の文字列)を貼って保存する。いま入っているものは
        見えない(署名済みの許可証なので、控えは発行元にある)。
      </Paragraph>
      <Space.Compact style={{ width: "100%" }}>
        <Input.TextArea
          rows={4}
          value={jwt}
          data-testid="license-input"
          placeholder="eyJ..."
          onChange={(e) => setJwt(e.target.value)}
        />
      </Space.Compact>
      <Flex justify="end" style={{ marginTop: 12 }}>
        <Button
          type="primary"
          data-testid="license-save"
          loading={save.isPending}
          disabled={jwt.trim().length === 0}
          onClick={() => save.mutate(jwt.trim())}
        >
          保存する
        </Button>
      </Flex>
    </div>
  );
}
